/* Number and date formatting, and the two thresholds the Overview panel judges against.
 * Every one of these returns an em dash for `null`, because printing an average over no rows
 * as 0 would be a claim the database never made.
 */

export const num = (v: number | null | undefined, digits = 0): string =>
  v == null || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });

/* Same rule as `num`: a field missing from an older server's answer is an em dash rather
 * than "NaN%", which would read as a fault in the page. */
const absent = (v: number | null | undefined): boolean => v == null || Number.isNaN(Number(v));

export const pct = (v: number | null | undefined, digits = 0): string =>
  absent(v) ? '—' : `${(Number(v) * 100).toFixed(digits)}%`;

export const ms = (v: number | null | undefined): string =>
  absent(v)
    ? '—'
    : Number(v) >= 1000
      ? `${(Number(v) / 1000).toFixed(1)}s`
      : `${Math.round(Number(v))}ms`;

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso.slice(0, 10)
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** "1 question" / "14 questions", for the Overview panel, which is made of sentences rather
 *  than the column headings that can write "question(s)". */
export const plural = (n: number, one: string, many = `${one}s`): string =>
  `${num(n)} ${Number(n) === 1 ? one : many}`;

/** Relative change between two windows, or null when the base is zero or missing. */
export const rel = (a: number | null | undefined, b: number | null | undefined): number | null =>
  a == null || !b ? null : (a - b) / b;

/** The four states a check on the Overview panel can be in. */
export type CheckStatus = 'good' | 'warn' | 'bad' | 'unknown';

/** A measure against the two thresholds that decide how it reads. Only the less-is-better
 *  direction exists; a check that improves upwards passes `1 - value`. */
export const band = (
  v: number | null | undefined,
  good: number,
  warn: number,
): CheckStatus => (v == null ? 'unknown' : v <= good ? 'good' : v <= warn ? 'warn' : 'bad');

/** Human labels for the classifier's routing verdicts, which are stored as database
 *  identifiers and not shown as-is. */
const KIND_LABELS: Record<string, string> = {
  system_question: 'Question about the systems',
  ticket_request: 'Asked for a support ticket',
  about_assistant: 'About the assistant',
  about_user: 'About themselves',
  small_talk: 'Greeting, thanks, small talk',
  off_topic: 'Off topic',
  unclassified: 'Not recorded (older rows)',
};

export const kindLabel = (k: string | null | undefined): string => KIND_LABELS[k ?? ''] || k || '—';

/** The order the message-mix chart reads in, with the assistant's actual job first, so the
 *  chart reads as a split rather than a ranking. */
export const KIND_ORDER = [
  'system_question',
  'ticket_request',
  'about_assistant',
  'about_user',
  'small_talk',
  'off_topic',
  'unclassified',
];

/** Where one kind sits in that order. A verdict added to the classifier and not yet here
 *  sorts last rather than first, which is what a bare `indexOf` did with its -1 — putting an
 *  unrecognised kind above the assistant's actual job. */
export const kindRank = (kind: string): number => {
  const at = KIND_ORDER.indexOf(kind);
  return at === -1 ? KIND_ORDER.length : at;
};

/** Who asked, in words. The stored values are `assistant/schemas.py`'s `UserRole`; a role
 *  added there and not here falls through to its stored spelling. */
const ROLE_LABELS: Record<string, string> = {
  student: 'Students',
  lecturer: 'Lecturers',
  coordinator: 'Coordinators',
  registrar: 'Registrar',
  admission: 'Admissions',
  finance: 'Finance',
  senior_academic: 'Senior academics',
  dean_of_students: 'Dean of students',
  cpao: 'CPAO',
  iqao: 'IQAO',
  ilao: 'ILAO',
  research_project_office: 'Research project office',
  research_reviewer: 'Research reviewers',
  exam_assessment: 'Exams and assessment',
  timetable_manager: 'Timetabling',
  ict_center: 'ICT centre',
  hr: 'HR',
  admin: 'Administrators',
  security: 'Security',
  reception: 'Reception',
  personnel: 'Personnel',
  // Not a role somebody holds: the host sent no verified one, so the assistant answered
  // from public documentation only.
  other: 'No verified role',
  unknown: 'No role recorded',
};

export const roleLabel = (r: string | null | undefined): string =>
  ROLE_LABELS[r ?? ''] || r || '—';

/** The shape of question the classifier put a turn in — `IntentCategory` in
 *  `assistant/schemas.py`, plus the two this dashboard adds for turns that never got one. */
const INTENT_LABELS: Record<string, string> = {
  how_to: 'How do I…',
  troubleshooting: 'Something is not working',
  permission_question: 'Am I allowed to…',
  information_request: 'What is…',
  deadline_inquiry: 'When is the deadline',
  feature_location: 'Where do I find it',
  error_resolution: 'An error message',
  general: 'General',
  unrecognised: 'Not classified',
  ...KIND_LABELS,
};

export const intentLabel = (i: string | null | undefined): string =>
  INTENT_LABELS[i ?? ''] || i || '—';

/** The thumbs-down reasons, spelled as the assistant's feedback panel spells them — see
 *  `FeedbackReason` in assistant/schemas.py. An unknown code is shown as stored. */
const REASON_LABELS: Record<string, string> = {
  incorrect: 'The answer was wrong',
  not_found: 'Did not find the information',
  irrelevant: 'Did not answer the question',
  unclear: 'The answer was unclear',
  other: 'Other',
};

export const reasonLabel = (r: string | null | undefined): string =>
  REASON_LABELS[r ?? ''] || r || '—';

/** The work each reason asks for: a wrong document, a missing one, the right one not
 *  retrieved, and a badly written one are four different jobs. A code with no entry here is
 *  skipped by the legend rather than given an invented remedy. */
const REASON_ACTIONS: Record<string, string> = {
  incorrect: 'correct the document it came from',
  not_found: 'write the document that is missing',
  irrelevant: 'the wrong document was retrieved',
  unclear: 'rewrite the document more clearly',
  other: 'read the comment — it says what happened',
};

export const reasonAction = (r: string | null | undefined): string | null =>
  REASON_ACTIONS[r ?? ''] ?? null;

/** `2026-W32` → `26-W32`. The century is the same for every bucket on the axis. */
export const weekLabel = (week: string): string => week.replace(/^\d{2}/, '');
